import axios, { AxiosError, AxiosResponse } from 'axios';
import { stringify } from 'querystring';
import simpleRestProvider from 'ra-data-simple-rest';
import { CreateParams, DataProvider, fetchUtils, UpdateParams } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';
import constants from '../constants';

axios.interceptors.response.use(
	(a: AxiosResponse) => {
		return a;
	},
	(error: AxiosError) => {
		if (error.response?.status === 401) {
			return refreshTheToken(error);
		} else throw error;
	},
);
export const refreshTheToken = async (error: AxiosError) => {
	const originalRequest: any = error.config;
	if (error.response?.status === 401 && !originalRequest._retry) {
		originalRequest._retry = true;
		try {
			const refresh_token = localStorage.getItem('refresh_token');
			const refresh_response = await axios.post(constants.API_URL + 'user/refresh', {
				refresh_token,
			});

			if (refresh_response.status === 201) {
				// 1) put token to LocalStorage
				localStorage.setItem('token', refresh_response.data);
				localStorage.setItem('token_exp', (new Date().getTime() + 4 * 60 * 1000).toString());
				// 2) Change Authorization header
				axios.defaults.headers.common['Authorization'] = 'Bearer ' + refresh_response.data;

				// 3) return originalRequest object with Axios.
				return axios({
					...originalRequest,
					data: originalRequest.data ? JSON.parse(originalRequest.data) : undefined,
					headers: { Authorization: 'Bearer ' + refresh_response.data },
				});
			}
		} catch (error) {}
	}
};
export const httpClient = (url: any, options: fetchUtils.Options = {}) => {
	if (!options.headers) {
		options.headers = new Headers({ Accept: 'application/json' });
	}
	const token = localStorage.getItem('token');
	options.user = {
		authenticated: Boolean(token),
		// use the token from local storage
		token: `Bearer ${token}`,
	};
	axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('token')}`;

	return axios(url, {
		method: options.method,
		data: options.body,
		headers: { 'Content-Type': 'application/json' },
	}).then(async (res) => ({
		status: res.status,
		json: res.data,
		headers: new Headers(res.headers),
		body: res.data,
	}));
};
const dataProvider = simpleRestProvider(constants.API_URL, httpClient);
const myDataProvider: DataProvider = {
	...dataProvider,
	getList: async (resource, params) => {
		// Anonymous feedback — page-based pagination on a dedicated endpoint.
		if (resource === 'anonymous-feedback') {
			const { page, perPage } = params.pagination;
			const url = `${constants.API_URL}anonymous-feedback?page=${page}&limit=${perPage}`;
			const { json } = await httpClient(url);
			const dataArray = json?.data ?? [];
			return {
				data: dataArray.map((item: any) => ({ ...item, id: item._id ?? item.id })),
				total: json?.total ?? dataArray.length,
			};
		}

		// GH-637 intervention logs — dedicated admin endpoint (newest first, page-based)
		if (resource === 'interventions') {
			const { page, perPage } = params.pagination;
			const filter = params.filter || {};
			const query = new URLSearchParams({ page: String(page), limit: String(perPage) });
			if (filter.decision) query.set('decision', filter.decision);
			if (filter.channel) query.set('channel', filter.channel);
			if (filter.company) query.set('company', filter.company);
			const url = `${constants.API_URL}interventions/admin/list?${query.toString()}`;
			const { json } = await httpClient(url);
			const dataArray = json?.data ?? [];
			return {
				data: dataArray.map((item: any) => ({ ...item, id: item._id ?? item.id })),
				total: json?.total ?? dataArray.length,
			};
		}

		// GH-749 — "מאגר מומחים לאירועים": ratings / lectures / popularity per expert (sorted + filtered server-side)
		if (resource === 'expert-ranking') {
			const { page, perPage } = params.pagination;
			const filter = params.filter || {};
			const query = new URLSearchParams({
				page: String(page),
				limit: String(perPage),
				sort: params.sort?.field || 'rating_avg',
				order: params.sort?.order === 'ASC' ? 'asc' : 'desc',
			});
			if (filter.q) query.set('q', filter.q);
			if (filter.categories?.length) query.set('categories', filter.categories.join(','));
			if (filter.kind) query.set('kind', filter.kind);
			if (filter.gender) query.set('gender', filter.gender);
			if (filter.minRatings) query.set('minRatings', String(filter.minRatings));
			if (filter.showAll) query.set('showAll', 'true');
			const { json } = await httpClient(`${constants.API_URL}admin/expert-ranking?${query.toString()}`);
			return { data: json?.data ?? [], total: json?.total ?? 0 };
		}
		if (resource === 'expert-ranking-categories') {
			const { json } = await httpClient(`${constants.API_URL}admin/expert-ranking/categories`);
			const dataArray = Array.isArray(json) ? json : [];
			return { data: dataArray, total: dataArray.length };
		}

		// Onboarding notes (member_notes) — dedicated admin endpoint, sorted by member createdAt
		if (resource === 'member-notes') {
			const { page, perPage } = params.pagination;
			const order = params.sort?.order === 'ASC' ? 'asc' : 'desc';
			const query = new URLSearchParams({ page: String(page), limit: String(perPage), order });
			if (params.filter?.company) query.set('company', params.filter.company);
			const url = `${constants.API_URL}admin/member-notes?${query.toString()}`;
			const { json } = await httpClient(url);
			const dataArray = json?.data ?? [];
			return {
				data: dataArray.map((item: any) => ({ ...item, id: item._id ?? item.id })),
				total: json?.total ?? dataArray.length,
			};
		}

		// Activity logs use a dedicated admin endpoint with page-only pagination and optional filters
		if (resource === 'activity-logs') {
			const { page } = params.pagination;
			const filter = params.filter || {};
			const query = new URLSearchParams({ page: String(page) });
			if (filter.userId) query.set('userId', filter.userId);
			if (filter.companyId) query.set('companyId', filter.companyId);
			if (filter.action) query.set('action', filter.action);
			// GH-841: category (e.g. SUPPORT_CIRCLE) + entity (a circle's _id = its whole log) filters
			if (filter.actionCategory) query.set('actionCategory', filter.actionCategory);
			if (filter.entityId) query.set('entityId', String(filter.entityId).trim());
			if (filter.entityType) query.set('entityType', filter.entityType);
			const url = `${constants.API_URL}admin/activity-logs?${query.toString()}`;
			const { json } = await httpClient(url);
			const dataArray = json?.data ?? [];
			const total = json?.total ?? 0;
			return {
				data: dataArray.map((item: any, index: number) => ({
					...item,
					id: item._id ?? item.id ?? `log-${(page - 1) * (json?.limit ?? 20) + index}`,
				})),
				total,
			};
		}

		const { page, perPage } = params.pagination;
		const { field, order } = params.sort;
		// GH-728 (Terra): phone search on the members list. Numbers are stored in mixed formats
		// (+97254…, 054…, with/without dashes), so match on the LAST 7 digits (after dropping a
		// leading 0 / 972), and only once ≥5 digits were typed. Sent as a $regex object so the
		// server keeps it as-is (a bare digit string could be mistaken for a date there).
		let filter = params.filter;
		if (resource === 'member' && filter && filter.phone !== undefined) {
			const { phone, ...rest } = filter;
			const digits = String(phone ?? '').replace(/\D/g, '').replace(/^(972|0)/, '');
			filter = digits.length >= 5 ? { ...rest, phone: { $regex: digits.slice(-7) } } : rest;
		}
		const query = {
			sort: JSON.stringify([field, order]),
			range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
			filter: JSON.stringify(filter),
		};
		const url = `${constants.API_URL}/${resource}?${stringify(query)}`;

		const { headers, json } = await httpClient(url);
		
		// Debug logging for specific resources
		if (resource === 'dating-events' || resource === 'events-coupons') {
			console.log(`${resource} API Response:`, json);
			console.log('API Response Type:', typeof json);
			console.log('Is Array?', Array.isArray(json));
			console.log('JSON Keys:', Object.keys(json || {}));
			console.log('Headers:', headers);
		}
		
		// Handle the specific API response structure for dating-events, events-coupons, member/get-searches, lifestyle-area/all, ai-program, and ai-program-prize
		let dataArray = [];
		let total = 0;
		
		if (resource === 'dating-events' || resource === 'events-coupons' || resource === 'member/get-searches' || resource === 'lifestyle-area/all' || resource === 'ai-program' || resource === 'ai-program-prize') {
			// Your API returns: { data: [...], pagination: {...}, success: true }
			if (Array.isArray(json)) {
				dataArray = json;
				total = json.length;
			} else if (json && typeof json === 'object') {
				// Extract data from the response structure
				dataArray = json.data || json.events || json.items || [];
				total = json.pagination?.total || json.total || json.count || dataArray.length;
			} else {
				dataArray = [];
				total = 0;
			}
			
		} else {
			// Default handling for other resources
			dataArray = json.data || json || [];
			total = parseInt(headers?.get('content-range')?.split('/').pop() || '0', 10);
		}
		
		return {
			data: dataArray.map((resource_1: any) => ({ ...resource_1, id: resource_1._id })),
			total: total,
		};
	},
	getOne: async (resource, params) => {
		// Map lifestyle-area/all to lifestyle-area for API endpoint
		const apiResource = resource === 'lifestyle-area/all' ? 'lifestyle-area' : resource;
		const { json } = await httpClient(`${constants.API_URL}/${apiResource}/${params.id}`);
		
		// Debug logging for specific resources
		if (resource === 'dating-events' || resource === 'events-coupons') {
			console.log(`${resource} getOne API Response:`, json);
		}
		
		// Handle the specific API response structure for dating-events and events-coupons
		if (resource === 'dating-events' || resource === 'events-coupons') {
			// Your API returns: { data: {...}, success: true }
			// Extract the actual data from the response
			const eventData = json.data || json;
			return {
				data: { ...eventData, id: eventData._id || eventData.id }
			};
		}
		
		// Default handling for other resources
		return {
			data: { ...json, id: json._id },
		};
	},

	getMany: async (resource, params) => {
		const query = {
			filter: JSON.stringify({ _id: params.ids }),
		};
		const url = `${constants.API_URL}/${resource}?${stringify(query)}`;
		const { json } = await httpClient(url);

		return {
			data: (json.data || json)?.map((resource: any) => ({ ...resource, id: resource._id })),
		};
	},

	getManyReference: (resource, params) => {
		const { page, perPage } = params.pagination;
		const { field, order } = params.sort;
		params = { ...params };
		const query = {
			sort: JSON.stringify([field, order]),
			range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
			filter: JSON.stringify({
				...params.filter,
				[params.target]: params.id,
			}),
		};
		const url = `${constants.API_URL}/${resource}?${stringify(query)}`;

		return httpClient(url).then(({ headers, json }) => ({
			data: json.map((resource: any) => ({ ...resource, id: resource._id })),
			total: parseInt(headers?.get('content-range')?.split('/').pop() || '0', 10),
		}));
	},

	update: async (resource: string, params: UpdateParams<any>) => {
		// Map lifestyle-area/all to lifestyle-area for API endpoint
		const apiResource = resource === 'lifestyle-area/all' ? 'lifestyle-area' : resource;
		
		if (resource === 'proactive/item') {
			const formData = createFormData(params.data);

			httpClient(`${constants.API_URL}${resource}/${params.id}`, {
				method: 'PUT',
				body: formData,
			}).then(({ json }) => ({ data: { ...json, id: json._id } }));
		}
		const pic = params.data.picture || params.data.image;
		const banner = params.data.banner_url;
		const prevImage = params.data.prev_image;

		if (!pic?.rawFile && !banner?.rawFile && !prevImage?.rawFile) {
			const { data } = await dataProvider.update(apiResource, params);
			return { data: { ...data, id: data.id } };
		}

		let transformedData = { ...params.data };

		if (pic?.rawFile) {
			transformedData.picture = await getTransformedImage(pic, params.data.name || params.data.title);
		}

		if (banner?.rawFile) {
			transformedData.banner_url = await getTransformedImage(banner, params.data.name || params.data.title || 'banner');
		}

		if (prevImage?.rawFile) {
			transformedData.prev_image = await getTransformedImage(prevImage, params.data.name || params.data.title || 'prev_image');
		}

		const { data } = await dataProvider.update(apiResource, {
			data: transformedData,
			id: params.id,
			previousData: params.data,
		});

		return { data: { ...data, id: data.id } };
	},

	create: async (resource: string, params: CreateParams<any>) => {
		// Map lifestyle-area/all to lifestyle-area for API endpoint
		const apiResource = resource === 'lifestyle-area/all' ? 'lifestyle-area' : resource;
		
		const pic = params.data.picture || params.data.image;
		const banner = params.data.banner_url;
		const prevImage = params.data.prev_image;

		if (!pic && !banner && !prevImage) {
			const { data } = await dataProvider.create(apiResource, params);
			// The API returns `_id` (no `id` virtual) on some resources, e.g. vod/category — without an id
			// react-admin's post-save redirect breaks even though the record was created.
			return { data: { ...data, id: data.id ?? data._id } };
		}

		let transformedData = { ...params.data };

		if (pic?.rawFile) {
			transformedData.picture = await getTransformedImage(pic, params.data.name || params.data.title);
		}

		if (banner?.rawFile) {
			transformedData.banner_url = await getTransformedImage(banner, params.data.name || params.data.title || 'banner');
		}

		if (prevImage?.rawFile) {
			transformedData.prev_image = await getTransformedImage(prevImage, params.data.name || params.data.title || 'prev_image');
		}

		const { data } = await dataProvider.create(apiResource, {
			data: transformedData,
		});
		return { data: { ...data, id: data.id ?? data._id } };
	},
};

const getTransformedImage = async (file: { rawFile: Blob }, name: string) => {
	const newPicture = file;
	if (newPicture) {
		const base64Picture = await convertFileToBase64(newPicture);
		return {
			src: base64Picture,
			title: `${name || uuidv4()}`,
		};
	}
};
const convertFileToBase64 = (file: { rawFile: Blob }) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;

		reader.readAsDataURL(file.rawFile);
	});

type FormDataParams<T> = {
	[K in keyof T]?: T[K] | { rawFile: File };
};

const createFormData = <T>(params: FormDataParams<T>): FormData => {
	const formData = new FormData();

	for (const key in params) {
		if (params.hasOwnProperty(key)) {
			const value = params[key];
			if ((value as any)?.rawFile) {
				formData.append(key, (value as any).rawFile);
			} else if (value !== undefined && value !== null) {
				formData.append(key, String(value));
			}
		}
	}
	return formData;
};

export default myDataProvider;
